import { initializeApp } from "firebase/app";
import { getToken, initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const SITE_KEY = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY || "";
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const DEBUG_TOKEN = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN || "";

let appCheckPromise = null;
let appCheckDisabledReason = "";

function hasFirebaseConfig() {
  return Boolean(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId && SITE_KEY);
}

function shouldAttachToUrl(input) {
  try {
    const raw = typeof input === "string" ? input : input?.url || "";
    const url = new URL(raw, window.location.origin);
    const apiBaseUrl = API_BASE ? new URL(API_BASE, window.location.origin) : null;
    const sameOriginApi = url.origin === window.location.origin && (url.pathname.startsWith("/api/") || url.pathname.startsWith("/auth/"));
    const configuredApi =
      apiBaseUrl &&
      url.origin === apiBaseUrl.origin &&
      (url.pathname.startsWith("/api/") || url.pathname.startsWith("/auth/"));
    return Boolean(sameOriginApi || configuredApi);
  } catch {
    return false;
  }
}

async function getAppCheck() {
  if (!hasFirebaseConfig()) {
    appCheckDisabledReason = "missing Firebase App Check env";
    return null;
  }
  if (!appCheckPromise) {
    appCheckPromise = Promise.resolve().then(() => {
      if (DEBUG_TOKEN && typeof window !== "undefined") {
        // Local dev only (.env.development). "true" asks the SDK to mint a debug token and print it to the
        // console (register it under Firebase console → App Check → Apps → Manage debug tokens); any other
        // value is used as an already-registered token. Production builds never set this variable.
        window.FIREBASE_APPCHECK_DEBUG_TOKEN = DEBUG_TOKEN.trim().toLowerCase() === "true" ? true : DEBUG_TOKEN;
      }
      const app = initializeApp(firebaseConfig);
      return initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(SITE_KEY),
        isTokenAutoRefreshEnabled: true,
      });
    });
  }
  try {
    return await appCheckPromise;
  } catch (err) {
    appCheckPromise = null;
    appCheckDisabledReason = err?.message || "App Check init failed";
    return null;
  }
}

async function appCheckToken() {
  const appCheck = await getAppCheck();
  if (!appCheck) return "";
  try {
    const result = await getToken(appCheck, false);
    return result?.token || "";
  } catch (err) {
    appCheckDisabledReason = err?.message || "App Check token failed";
    return "";
  }
}

export function installAppCheckFetch() {
  if (typeof window === "undefined" || window.__jfAppCheckFetchInstalled) return;
  window.__jfAppCheckFetchInstalled = true;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    if (!shouldAttachToUrl(input)) return nativeFetch(input, init);
    const token = await appCheckToken();
    if (!token) return nativeFetch(input, init);
    const headers = new Headers(init.headers || (typeof input !== "string" ? input.headers : undefined) || {});
    headers.set("X-Firebase-AppCheck", token);
    return nativeFetch(input, { ...init, headers });
  };
}

export function appCheckStatus() {
  return {
    configured: hasFirebaseConfig(),
    disabledReason: appCheckDisabledReason,
  };
}
